if (!customElements.get('tertiary-category-menu')) {
  class TertiaryCategoryMenu extends HTMLElement {
    static requests = new Map();

    connectedCallback() {
      if (this.button) return;
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.link = this.querySelector('a');
      this.panel = this.querySelector('[data-tertiary-panel]');
      this.button.hidden = false;
      // Dawn treats drawer buttons as back buttons; consume our arrow first.
      this.addEventListener('click', event => {
        if (!this.button.contains(event.target)) return;
        event.stopPropagation();
        this.toggle();
      }, true);
      this.addEventListener('keyup', event => {
        if (event.key !== 'Escape' || this.panel.hidden) return;
        event.stopPropagation();
        this.toggle();
        this.button.focus();
      });
      this.closest('details')?.addEventListener('toggle', event => {
        if (!event.target.open && !this.panel.hidden) this.toggle();
      });
    }

    toggle() {
      if (!this.panel.hidden && this.panel.contains(document.activeElement)) this.button.focus();
      this.panel.hidden = !this.panel.hidden;
      this.button.setAttribute('aria-expanded', String(!this.panel.hidden));
      this.panel.replaceChildren();
      if (!this.panel.hidden) this.load();
      this.refreshDrawerFocus();
    }

    refreshDrawerFocus() {
      const drawer = this.closest('.menu-drawer__submenu');
      if (drawer && this.contains(document.activeElement) && typeof trapFocus === 'function') {
        trapFocus(drawer, document.activeElement);
      }
    }

    async load() {
      const status = document.createElement('p');
      status.setAttribute('role', 'status');
      status.textContent = 'Loading subcategories…';
      this.panel.append(status);
      const url = new URL(this.link.href);
      url.searchParams.set('section_id', 'tertiary-category-links');
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const key = url.href + ':' + this.dataset.branch;
      try {
        if (!TertiaryCategoryMenu.requests.has(key)) {
          const request = (async () => {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 8000);
            try {
              const response = await fetch(url.href, {signal: controller.signal});
              if (!response.ok) throw new Error('Category request failed');
              const html = new DOMParser().parseFromString(await response.text(), 'text/html');
              const branch = [...html.querySelectorAll('[data-tertiary-branch]')]
                .find(node => node.dataset.tertiaryBranch === this.dataset.branch);
              const links = [...(branch?.querySelectorAll('a') || [])].map(link => {
                const href = link.getAttribute('href');
                if (!href || !link.textContent.trim()) throw new Error('Invalid category link');
                const target = new URL(href, location.origin);
                if (target.origin !== location.origin || !['http:', 'https:'].includes(target.protocol)) {
                  throw new Error('Invalid category destination');
                }
                return {href: target.pathname + target.search + target.hash, title: link.textContent};
              });
              if (!links.length) throw new Error('No subcategories returned');
              return links;
            } finally {
              clearTimeout(timeout);
            }
          })();
          TertiaryCategoryMenu.requests.set(key, request);
          request.catch(() => TertiaryCategoryMenu.requests.delete(key));
        }
        const links = await TertiaryCategoryMenu.requests.get(key);
        if (this.panel.hidden) return;
        const list = document.createElement('ul');
        list.className = 'list-unstyled';
        list.setAttribute('role', 'list');
        for (const link of links) {
          const item = document.createElement('li');
          const anchor = document.createElement('a');
          anchor.className = 'link link--text focus-inset';
          anchor.href = link.href;
          anchor.textContent = link.title;
          item.append(anchor);
          list.append(item);
        }
        status.textContent = links.length + ' subcategories loaded.';
        status.className = 'visually-hidden';
        this.panel.replaceChildren(status, list);
      } catch {
        if (this.panel.hidden) return;
        status.textContent = 'Couldn’t load subcategories.';
        const fallback = this.link.cloneNode(false);
        fallback.removeAttribute('id');
        fallback.textContent = 'Browse ' + this.link.textContent.trim() + ' →';
        this.panel.replaceChildren(status, fallback);
      }
      this.refreshDrawerFocus();
    }
  }

  customElements.define('tertiary-category-menu', TertiaryCategoryMenu);
}
